import React, { useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { ListTransFileRecs } from "../../util/API/batch";
import CustomAlert from "../UI/CustomAlert/CustomAlert";

const getPayrollFileName = (inputFilename, statusCode) => {
  const baseName = (inputFilename || "batch").replace(/\.csv$/i, "");
  const code = String(statusCode || "");
  let suffix = "";
  if (code === "3") suffix = "_success";
  else if (code === "2") suffix = "_err";
  return `${baseName}_records${suffix}.csv`;
};

const cleanNotes = (notes) =>
  notes
    ? notes
        
        .replace(/&amp;amp;/g, "&")
        .replace(/&amp;#039;/g, "'")
        .replace(/&amp;#39;/g, "'")
        
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&nbsp;/g, " ")
        .split("\n")
        .filter(Boolean)
        .join(" • ")
    : "—";

function SuperAdminDownloadButton({ batch }) {
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
    header: "Success"
  });
  const [downloading, setDownloading] = useState(false);

  if (!batch || !batch.id) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await ListTransFileRecs(batch.id);

      if (res.callStatus !== "1") {
        setAlert({
          open: true,
          severity: "error",
          header: "Download Failed",
          message: res.payload?.msg || "Could not load records from server."
        });
        return;
      }

      const rawArray = res.payload?.record || [];
      const recordsArray = Array.isArray(rawArray) ? rawArray : rawArray ? [rawArray] : [];
      const allRecords = recordsArray.map(item => item.Transfilerecs).filter(Boolean);

      if (allRecords.length === 0) {
        setAlert({
          open: true,
          severity: "info",
          header: "No Data",
          message: "This batch has no records to download."
        });
        return;
      }

      const headers = [
        "First Name", "Last Name", "Email", "Phone", "Amount", "Currency",
        "Address Line 1", "Address Line 2", "Country", "State", "Zip Code",
        "Date of Birth", "Comment", "Purpose of Account",
        "Account Add Success Message", "Fund Add Success Message"  // ← updated columns
      ];

      const rows = allRecords.map((r) => {
        const identifier = r.phoneno ? r.phoneno : r.email;

        const accountMessage = r.usercreated === "0"
          ? `${identifier}@4pay.ph user already exists`
          : r.usercreated === "1"
          ? `${identifier} user created successfully`
          : "";

        return [
          (r.firstname || "").replace(/"/g, '""'),
          (r.lastname || "").replace(/"/g, '""'),
          (r.email || "").replace(/"/g, '""'),
          (r.phoneno || "").replace(/"/g, '""'),
          r.amount || "0.00",
          r.currency || "",
          (r.address1 || "").replace(/"/g, '""'),
          (r.address2 || "").replace(/"/g, '""'),
          r.country || "",
          r.state || "",
          r.zipcode || "",
          r.birthdate || "",
          (r.comment ? cleanNotes(r.comment) : "").replace(/"/g, '""'),
          (r.purposeofaccount || "").replace(/"/g, '""'),
          accountMessage.replace(/"/g, '""'),   // ← Account Add Success Message
          (r.msg || "").replace(/"/g, '""')     // ← Fund Add Success Message
        ];
      });

      const csv = [headers.join(","), ...rows.map(row => row.map(cell => `"${cell}"`).join(","))].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getPayrollFileName(batch.fileName, batch.statusCode); // ← status-based filename
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 40000);

      setAlert({
        open: true,
        severity: "success",
        header: "Downloaded",
        message: "Records CSV has been downloaded."
      });
    } catch (err) {
      console.error("Download failed:", err);
      setAlert({
        open: true,
        severity: "error",
        header: "Download Failed",
        message: "Error generating CSV. Please try again."
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={handleDownload}
        disabled={downloading}
        startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : null}
        sx={{
          backgroundColor: "#145388",
          "&:hover": { backgroundColor: "#0f3d6e" },
        }}
      >
        {downloading ? "Downloading..." : "Download CSV"}
      </Button>

      <CustomAlert
        severity={alert.severity}
        show={alert.open}
        header={alert.header}
        message={alert.message}
        onClick={() => setAlert({ ...alert, open: false })}
        autoclose={alert.severity === "success"}
      />
    </>
  );
}

export default SuperAdminDownloadButton;